'use client';
import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useSite, useT } from '../lib/i18n';
import { useLayerLock } from '../lib/useLayerLock';
import { Icon } from './Icon';

/**
 * 명령 팔레트 — ⌘K / Ctrl+K (2026-08-22 UX 채택안 #12).
 *
 * 어디서든 단축키로 검색을 띄운다. 헤더의 검색 버튼도 이걸 연다 — 지금까지 헤더 검색은
 * "입력만 받는" 상태였는데(③ 강좌 전이라), 팔레트가 그 실행까지 맡는다.
 *
 *  · 입력하면 강좌·교재·강사를 **이름으로 실시간 검색**(BFF, 300ms 디바운스)
 *  · Enter → 강좌 목록 검색 결과(/lectures?q=)로
 *  · ↑↓ 로 고르고 Enter 로 이동, Esc 로 닫기
 *
 * 열려 있는 동안 스크롤을 잠근다. 딤 클릭으로 닫는 것은 이 화면에 한해 허용한다 —
 * 약관·확인 레이어와 달리 잃을 입력이 검색어뿐이고, 검색 UI 관례가 그렇다.
 */
interface Row {
  key: string;
  icon: string;
  label: string;
  hint: string;
  href: string;
}

const OPEN_EVENT = 'app:palette';
/** 헤더 검색 버튼 등 다른 곳에서 연다 */
export function openPalette() {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT));
}

export function CommandPalette() {
  const t = useT();
  const { loggedIn } = useSite();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  /** 검색 결과만 상태로 둔다 — 고정 항목은 렌더할 때 계산한다(상태에 담으면 되먹임이 생긴다) */
  const [found, setFound] = useState<Row[] | null>(null);
  const [cursor, setCursor] = useState(0);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const seqRef = useRef(0);

  useLayerLock(open, () => setOpen(false));

  // 단축키 + 외부 열기 이벤트
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    const onOpen = () => setOpen(true);
    document.addEventListener('keydown', onKey);
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener(OPEN_EVENT, onOpen);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    setQ('');
    setFound(null);
    setCursor(0);
    // 레이어가 그려진 다음 틱에 포커스
    const id = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(id);
  }, [open]);

  /**
   * 검색 없이 보여 줄 이동 항목 — **상태가 아니라 렌더 계산이다.**
   * 전에는 이걸 `setRows` 로 상태에 담았는데, 효과가 돌 때마다 새 배열을 넣고 그것이 다시
   * 렌더를 부르는 되먹임이 됐다(무한 루프). 계산해서 바로 쓰면 그런 고리가 생기지 않는다.
   */
  const fixedRows: Row[] = [
    { key: 'go-lectures', icon: 'book-open', label: t('prd.lectureList'), hint: t('pal.go'), href: '/lectures' },
    { key: 'go-books', icon: 'book', label: t('prd.bookList'), hint: t('pal.go'), href: '/books' },
    { key: 'go-cart', icon: 'shopping-cart', label: t('shell.cart'), hint: t('pal.go'), href: '/cart' },
    ...(loggedIn
      ? [{ key: 'go-my', icon: 'graduation-cap', label: t('my.classroom'), hint: t('pal.go'), href: '/my' }]
      : [{ key: 'go-login', icon: 'log-in', label: t('shell.login'), hint: t('pal.go'), href: '/login' }]),
  ];

  /*
   * 실시간 검색 — 300ms 디바운스, 늦게 온 옛 응답은 버린다.
   * 의존성은 **원시값만**(q·open) 둔다 — 함수·배열을 넣으면 그 신원이 바뀔 때마다 효과가
   * 다시 돌아 루프가 된다. 안에서 쓰는 라벨은 `t` 지만 검색 결과에만 붙는 표기라
   * 언어가 바뀌면 어차피 화면 전체가 다시 그려진다.
   */
  useEffect(() => {
    const text = q.trim();
    if (!open || !text) { setFound(null); return; }
    const seq = ++seqRef.current;
    setBusy(true);
    const timer = setTimeout(async () => {
      try {
        const [prd, tch] = await Promise.all([
          fetch(`/api/products?q=${encodeURIComponent(text)}&size=6`).then((r) => (r.ok ? r.json() : { rows: [] })),
          fetch(`/api/teachers?q=${encodeURIComponent(text)}&size=4`).then((r) => (r.ok ? r.json() : { rows: [] })),
        ]) as [
          { rows: { id: number; name: string; type: string }[] },
          { rows: { id: number; name: string | null }[] },
        ];
        if (seq !== seqRef.current) return; // 더 새 검색이 나갔다
        setFound([
          ...prd.rows.map((p) => ({
            key: `p${p.id}`, icon: p.type === 'BOOK' ? 'book' : 'book-open',
            label: p.name, hint: p.type === 'BOOK' ? t('prd.type.book') : t('prd.type.lecture'),
            href: p.type === 'BOOK' ? `/books/${p.id}` : `/lectures/${p.id}`,
          })),
          ...tch.rows.map((x) => ({
            key: `t${x.id}`, icon: 'user', label: x.name ?? '', hint: t('pal.teacher'), href: `/teachers/${x.id}`,
          })),
        ]);
        setCursor(0);
      } catch {
        if (seq === seqRef.current) setFound([]);
      } finally {
        if (seq === seqRef.current) setBusy(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [q, open]); // eslint-disable-line react-hooks/exhaustive-deps

  /** 지금 그릴 목록 — 검색 전이면 이동 항목, 검색 중이면 결과 */
  const rows = found ?? fixedRows;

  const go = (href: string) => {
    setOpen(false);
    router.push(href);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(c + 1, rows.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      const picked = rows[cursor];
      if (picked) go(picked.href);
      else if (q.trim()) go(`/lectures?q=${encodeURIComponent(q.trim())}`);
    }
  };

  if (!open) return null;
  return (
    <>
      <div className="pal-dim" onClick={() => setOpen(false)} />
      <div className="pal" role="dialog" aria-modal="true" aria-label={t('prd.search')}>
        <div className="pal-input">
          <Icon name="search" size={16} />
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKeyDown}
            placeholder={t('shell.searchPh')} />
          {busy && <span className="pal-busy" aria-hidden />}
          {/* ESC 배지는 눌러서도 닫힌다(2026-08-26 — 관리자 팔레트와 같은 규칙) */}
          <button type="button" className="pal-esc" onClick={() => setOpen(false)}
            title={t('action.close')} aria-label={t('action.close')}>
            <kbd>ESC</kbd>
          </button>
        </div>
        <div className="pal-list">
          {rows.map((r, i) => (
            <button key={r.key} className={`pal-row${i === cursor ? ' active' : ''}`}
              onMouseEnter={() => setCursor(i)} onClick={() => go(r.href)}>
              <Icon name={r.icon} size={15} />
              <span>{r.label}</span>
              <em>{r.hint}</em>
            </button>
          ))}
          {!rows.length && !busy && (
            <button className="pal-row active" onClick={() => q.trim() && go(`/lectures?q=${encodeURIComponent(q.trim())}`)}>
              <Icon name="search" size={15} />
              <span>{t('pal.searchAll', { q })}</span>
            </button>
          )}
        </div>
      </div>
    </>
  );
}
